/**
 * Chat feature: messages per slide, render list, send.
 */

import { on, EVENTS } from '../core/events';
import type { LecturaHostAdapter, DrawerView, ChatStorage, ChatRoleService, AIResourcesService, AIResourceCitation } from '../core/types';
import type { ChatMentionNotifyService } from '../services/chat-mention-notify';
import { formatChatMarkdown } from './chat-markdown';
import { lecturaHapticChatIncoming } from '../lib/haptics';
import { getRevealSpeakerNotesPlainTextFromSlide } from '../services/remote/v1/reveal-bridge';
import { showRemoteEmoteToast } from './remote/emote-toast';

/** Floating on-deck cue for new slide chat (same overlay path as remote audience emotes). */
const CHAT_MESSAGE_TOAST_EMOJI = '💬';

/** `window.__LECTURA_DEBUG_CHAT_TOAST__` or URL `?lecturaDebugChat=1` (and hash query if present). */
export function isChatToastDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if ((window as unknown as { __LECTURA_DEBUG_CHAT_TOAST__?: boolean }).__LECTURA_DEBUG_CHAT_TOAST__) return true;
    const on = (s: string): boolean => {
      const p = new URLSearchParams(s);
      const v = p.get('lecturaDebugChat');
      return v === '1' || v === 'true';
    };
    if (on(window.location.search || '')) return true;
    const hash = window.location.hash || '';
    const hi = hash.indexOf('?');
    if (hi >= 0 && on(hash.slice(hi + 1))) return true;
    return false;
  } catch {
    return false;
  }
}

function chatToastVerbose(message: string, data?: Record<string, unknown>): void {
  if (!isChatToastDebugEnabled()) return;
  if (typeof console !== 'undefined' && console.info) {
    console.info(`[lectura][chat-toast] ${message}`, data ?? '');
  }
}

/** Extra context for @ai: same sources as Speaker notes (MQTT state on remote, DOM on deck). Parent /remote has no slide markup — iframe only. */
const AI_SLIDE_CONTEXT_MAX = 3500;

function buildSlideContextForAi(adapter: LecturaHostAdapter): string {
  const parts: string[] = [];
  const title =
    typeof adapter.getSlideTitlePlainText === 'function'
      ? String(adapter.getSlideTitlePlainText() || '').trim().slice(0, 300)
      : '';
  if (title) parts.push(`Slide title: ${title}`);
  let notes = '';
  if (typeof adapter.getSpeakerNotesPlainText === 'function') {
    notes = String(adapter.getSpeakerNotesPlainText() || '').trim();
  } else {
    notes = getRevealSpeakerNotesPlainTextFromSlide(adapter.getCurrentSlide?.() ?? null);
  }
  if (notes) parts.push(`Speaker notes (excerpt): ${notes.slice(0, 2800)}`);
  const combined = parts.join('\n');
  if (combined.length <= AI_SLIDE_CONTEXT_MAX) return combined;
  return combined.slice(0, AI_SLIDE_CONTEXT_MAX) + '…';
}

function escapeHtml(s: string | null | undefined): string {
  if (s == null || s === '') return '';
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

function toDisplayName(raw: string | null | undefined, fallbackUid?: string): string {
  const value = String(raw || '').trim();
  if (!value) return String(fallbackUid || 'Anonymous');
  const at = value.indexOf('@');
  if (at > 0) {
    const local = value.slice(0, at).replace(/[._-]+/g, ' ').trim();
    if (local) {
      return local.replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }
  return value;
}

/** @lectura / @presenter / @ai only as standalone tokens (after start or whitespace). */
function hasMentionToken(trimmed: string, token: '@lectura' | '@presenter'): boolean {
  const re =
    token === '@lectura' ? /(?:^|[\s\n])@lectura\b/i : /(?:^|[\s\n])@presenter\b/i;
  return re.test(trimmed);
}

/** @ai only after line start or whitespace — avoids matching user@ai.com */
function extractAiQuestionAfterAtAi(trimmed: string): string | null {
  const withBody = trimmed.match(/(?:^|[\s\n])@ai\s+([\s\S]+)/i);
  if (withBody && String(withBody[1]).trim()) return String(withBody[1]).trim();
  if (/(?:^|[\s\n])@ai\s*$/i.test(trimmed)) return '';
  return null;
}

function isReplyingToLecturaAi(r: ReplyingTo | null): boolean {
  if (!r) return false;
  if (r.fromAi === true) return true;
  const a = String(r.author || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return a === 'lectura ai';
}

interface ReplyingTo {
  id: string;
  author: string;
  text: string;
  /** True when replying to a Lectura AI message — follow-ups go to @ai without retyping @ai. */
  fromAi?: boolean;
}

interface ChatUiOptions {
  fabBadge?: HTMLElement;
  roleService?: ChatRoleService;
  ai?: AIResourcesService;
  chatMentionNotify?: ChatMentionNotifyService;
}

const CHAT_SCROLL_BOTTOM_THRESHOLD_PX = 24;
const CHAT_MENTION_OPTIONS = [
  { token: '@presenter', title: 'Presenter', subtitle: 'Notify presentation presenters' },
  { token: '@lectura', title: 'Lectura', subtitle: 'Create a support ticket' },
  { token: '@ai', title: 'AI', subtitle: 'Ask AI about slides/materials' }
] as const;

const EMPTY_CHAT_SUGGESTIONS = [
  '🧩 Can we pause and explain this in simpler words?',
  '📝 Which part of this slide is most likely exam material?',
  '💡 Can someone give a practical example of this concept?',
  "🪜 I'm stuck on one step here — can we walk through it together?",
  '🔁 Can we compare this with what we learned in the previous chapter?',
  '⭐ What is the one key idea we should remember from this slide?',
  '🎯 Can we do a quick mini-example before moving on?',
  '⚠️ What common mistake should we avoid with this topic?',
  '✂️ Could someone restate this in one sentence?',
  '📂 How would this show up in a real assignment or project?',
  '🐢 Can we slow down and repeat the last part once more?',
  '🧠 Which definition on this slide is most important to memorize?',
  // Playful & social
  '☕ Is this slide genius or am I just caffeinated?',
  '🍿 Rate this slide: 🍿 or 😴?',
  '🙈 Anyone else pretending to understand until someone asks a question?',
  '🎬 Plot twist: what if we explained this like a movie trailer?',
  '🧠💨 Emergency: brain full. Can we label what actually matters?',
  '👏🔥 Hot take: standing ovation or full redo?',
  // @ai — RAG assistant
  '@ai 🤖 Summarize this slide like I am five (but respect my intelligence).',
  '@ai 🕵️ What would a sneaky exam question on this material look like?',
  '@ai ✨ Connect this slide to something from the extra materials — surprise me.',
  '@ai 🧯 Roast this concept gently, then teach it properly.',
  '@ai 🐦 If this were a tweet, what is the one line we should remember?',
  '@ai 🥊 Debate club: strongest counterargument, then the rebuttal.',
  '@ai 🍽️ Translate this into “I can explain it at dinner” language.',
  // @presenter
  '@presenter 💚 We love you — please slow down this one bit?',
  '@presenter 📣 Can we get a real-world war story for this slide?',
  '@presenter 🎲 Permission to ask a chaotic good follow-up?',
  '@presenter 🔊 This slide slaps — can we linger 30 seconds longer?',
  '@presenter 🧭 Emergency vibe check: are we on track for the big idea?',
  // @lectura — bugs, feedback, appraisal (support ticket)
  '@lectura 🐛 Bug report: [what broke] on [browser] — steps below, patience included.',
  '@lectura 🕵️ Mystery bug: it only happens when I do X then Y. Halp?',
  '@lectura 💬 Feedback: one thing I wish Lectura did differently is…',
  '@lectura ✨ Feature pitch (short): could we have ___? Would make my life 10× easier.',
  '@lectura 🏆 Appraisal: chat + drawer are genuinely saving this semester. Thank you.',
  '@lectura 🫶 Love letter to the product team: you crushed the UX on this flow.',
  '@lectura 📋 Constructive note: [area] feels clunky — happy to explain in detail.',
  '@lectura 🎁 Bug bounty of one (1) virtual high-five if you squash this weird glitch.'
];
const EMPTY_CHAT_SUGGESTIONS_COUNT = 2;

function formatMessageTime(createdAt: number | undefined): { label: string; full: string } {
  if (!createdAt || !Number.isFinite(createdAt)) return { label: '', full: '' };
  const date = new Date(createdAt);
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const label = isToday
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { year: 'numeric', month: 'short', day: '2-digit' });
  const full = date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  return { label, full };
}

function getRandomSuggestions(count: number): string[] {
  if (EMPTY_CHAT_SUGGESTIONS.length <= count) return EMPTY_CHAT_SUGGESTIONS.slice();
  const pool = EMPTY_CHAT_SUGGESTIONS.slice();
  const result: string[] = [];
  while (result.length < count && pool.length > 0) {
    const idx = Math.floor(Math.random() * pool.length);
    result.push(pool[idx]);
    pool.splice(idx, 1);
  }
  return result;
}

function renderSuggestionButtonsHtml(suggestions: string[]): string {
  return suggestions
    .map((s) => {
      const safe = escapeHtml(s);
      return '<button type="button" class="rf-chat-empty-suggestion" data-suggestion="' + safe + '">' + safe + '</button>';
    })
    .join('');
}

function getLecturaFaviconHref(): string {
  const iconLink = document.querySelector('link[rel~="icon"], link[rel="shortcut icon"]') as HTMLLinkElement | null;
  const rawHref = String(iconLink?.getAttribute('href') || iconLink?.href || '/favicon.svg').trim();
  if (!rawHref) return '/favicon.svg';
  if (rawHref.startsWith('data:') || rawHref.startsWith('http://') || rawHref.startsWith('https://')) return rawHref;
  try {
    return new URL(rawHref, window.location.href).href;
  } catch {
    return '/favicon.svg';
  }
}

export function registerChat(
  adapter: LecturaHostAdapter,
  drawerView: DrawerView,
  storage: ChatStorage,
  ui: ChatUiOptions = {}
): void {
  if (isChatToastDebugEnabled()) {
    console.info(
      '[lectura][chat-toast] registerChat — presentation slide chat, unread badge, and 💬 toast run from chat.ts (Firestore subscribeMessages)'
    );
  }
  const chatMentionNotify = ui.chatMentionNotify;
  const READ_WATERMARK_STORAGE_KEY = 'lectura-next-chat-read-watermark-v1';
  const fabBadge = ui.fabBadge ?? null;
  const roleService = ui.roleService;
  const ai = ui.ai;
  let replyingTo: ReplyingTo | null = null;
  let mentionMenuEl: HTMLElement | null = null;
  let mentionVisibleOptions: ReadonlyArray<(typeof CHAT_MENTION_OPTIONS)[number]> = [];
  let mentionActiveIndex = 0;

  const unreadCountByKey = new Map<string, number>();
  const totalCountByKey = new Map<string, number>();
  const readWatermarkByKey = new Map<string, number>();
  const emptySuggestionsByKey = new Map<string, string[]>();
  /** Per `userReadKey`: applied the "no saved read cursor → treat current backlog as read" bootstrap. */
  const bootstrappedByKey = new Set<string>();
  /**
   * Per slide (`keyFor`): first Firestore snapshot had at least one message.
   * If the first snapshot is empty (still loading), we must not bootstrap read state with maxCreatedAt=0
   * or we lock the slide as "bootstrapped" and every later message looks unread after refresh.
   */
  const chatFirstSnapshotHadMessagesByKey = new Map<string, boolean>();
  /** Per slide: max `createdAt` we have already processed for floating toast + haptic (avoids tail-only misses). */
  const lastProcessedChatToastMaxCreatedAtByKey = new Map<string, number>();
  let drawerOpen = false;
  let hasBoundChatScrollEvents = false;
  let unsubscribeMessages: (() => void) | null = null;
  let animateEmptySuggestionsOnNextRender = true;

  function getChatMessagesEl(): HTMLElement | null {
    return (drawerView.getChatMessages?.() as HTMLElement | null | undefined) || null;
  }

  function getChatScrollBottomBtnEl(): HTMLButtonElement | null {
    return (drawerView.getChatScrollBottomBtn?.() as HTMLButtonElement | null | undefined) || null;
  }

  function isChatNearBottom(el: HTMLElement): boolean {
    const distance = el.scrollHeight - (el.scrollTop + el.clientHeight);
    return distance <= CHAT_SCROLL_BOTTOM_THRESHOLD_PX;
  }

  function scrollChatToBottom(el: HTMLElement, smooth = false): void {
    if (smooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      return;
    }
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
      return;
    }
    el.scrollTop = el.scrollHeight;
  }

  function pinChatToBottomNow(): void {
    const el = getChatMessagesEl();
    if (!el) return;
    // Keep the view pinned even when async render/measure work updates height shortly after.
    scrollChatToBottom(el, false);
    window.requestAnimationFrame(() => scrollChatToBottom(el, false));
    window.setTimeout(() => scrollChatToBottom(el, false), 80);
  }

  function updateScrollBottomButtonVisibility(): void {
    const messagesEl = getChatMessagesEl();
    const btn = getChatScrollBottomBtnEl();
    if (!messagesEl || !btn) return;
    const hasOverflow = messagesEl.scrollHeight - messagesEl.clientHeight > 2;
    const shouldShow = hasOverflow && !isChatNearBottom(messagesEl);
    btn.classList.toggle('rf-hidden', !shouldShow);
  }

  function bindChatScrollEvents(): void {
    if (hasBoundChatScrollEvents) return;
    const messagesEl = getChatMessagesEl();
    const btn = getChatScrollBottomBtnEl();
    if (!messagesEl || !btn) return;
    hasBoundChatScrollEvents = true;
    messagesEl.addEventListener('scroll', () => updateScrollBottomButtonVisibility(), { passive: true });
    btn.addEventListener('click', () => {
      scrollChatToBottom(messagesEl, true);
      window.setTimeout(() => updateScrollBottomButtonVisibility(), 180);
    });
  }

  function keyFor(presId: string, slideIndex: number): string {
    return presId + '|' + slideIndex;
  }

  function getStableEmptySuggestions(presId: string, slideIndex: number): string[] {
    const k = keyFor(presId, slideIndex);
    const cached = emptySuggestionsByKey.get(k);
    if (cached && cached.length > 0) return cached;
    const next = getRandomSuggestions(EMPTY_CHAT_SUGGESTIONS_COUNT);
    emptySuggestionsByKey.set(k, next);
    return next;
  }

  function reshuffleEmptySuggestions(presId: string, slideIndex: number): string[] {
    const k = keyFor(presId, slideIndex);
    const next = getRandomSuggestions(EMPTY_CHAT_SUGGESTIONS_COUNT);
    emptySuggestionsByKey.set(k, next);
    return next;
  }

  function userReadKey(presId: string, slideIndex: number): string {
    const uid = adapter.getCurrentUser()?.uid || 'anon';
    return uid + '|' + presId + '|' + slideIndex;
  }

  function loadReadWatermarks(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(READ_WATERMARK_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      Object.keys(parsed).forEach((k) => {
        const n = Number(parsed[k] ?? 0);
        if (Number.isFinite(n) && n > 0) readWatermarkByKey.set(k, n);
      });
    } catch {
      // ignore invalid local storage data
    }
  }

  function persistReadWatermarks(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const data: Record<string, number> = {};
      readWatermarkByKey.forEach((v, k) => {
        if (Number.isFinite(v) && v > 0) data[k] = v;
      });
      localStorage.setItem(READ_WATERMARK_STORAGE_KEY, JSON.stringify(data));
    } catch {
      // ignore local storage write issues
    }
  }

  function getReadWatermark(presId: string, slideIndex: number): number {
    return readWatermarkByKey.get(userReadKey(presId, slideIndex)) ?? 0;
  }

  function setReadWatermark(presId: string, slideIndex: number, ts: number): void {
    const key = userReadKey(presId, slideIndex);
    const prev = readWatermarkByKey.get(key) ?? 0;
    const next = Math.max(prev, ts || 0);
    readWatermarkByKey.set(key, next);
    persistReadWatermarks();
  }

  function updateBadges(): void {
    const currentKey = keyFor(adapter.getPresId(), adapter.getSlideIndex());
    const totalForSlide = Math.max(0, totalCountByKey.get(currentKey) ?? 0);
    const unreadForSlide = Math.max(0, unreadCountByKey.get(currentKey) ?? 0);
    const displayCount = unreadForSlide > 0 ? unreadForSlide : totalForSlide;
    const label = displayCount > 99 ? '99+' : String(displayCount);
    const hasAnyMessages = totalForSlide > 0;
    const hasUnread = unreadForSlide > 0;

    const tabBadge = drawerView.getDrawerTabChatBadge?.();
    if (tabBadge) {
      tabBadge.textContent = hasAnyMessages ? label : '';
      tabBadge.classList.toggle('rf-hidden', !hasAnyMessages);
      tabBadge.classList.toggle('rf-badge-unread', hasUnread);
      tabBadge.classList.toggle('rf-badge-empty', hasAnyMessages && !hasUnread);
    }

    if (fabBadge) {
      fabBadge.textContent = hasAnyMessages ? label : '';
      fabBadge.classList.toggle('rf-hidden', !hasAnyMessages);
      fabBadge.classList.toggle('rf-badge-unread', hasUnread);
      fabBadge.classList.toggle('rf-badge-empty', hasAnyMessages && !hasUnread);
    }
  }

  function updateReplyPreview(): void {
    const wrap = drawerView.getChatReplyPreview?.();
    const textEl = drawerView.getChatReplyPreviewText?.();
    if (!wrap || !textEl) return;
    if (replyingTo) {
      const short = replyingTo.text.length > 60 ? replyingTo.text.slice(0, 60) + '…' : replyingTo.text;
      textEl.textContent = 'Replying to ' + replyingTo.author + ': ' + short;
      wrap.classList.remove('rf-hidden');
    } else {
      textEl.textContent = '';
      wrap.classList.add('rf-hidden');
    }
  }

  function ensureMentionMenu(input: HTMLTextAreaElement): HTMLElement {
    if (mentionMenuEl) return mentionMenuEl;
    const form = input.closest('.rf-chat-form') as HTMLElement | null;
    const menu = document.createElement('div');
    menu.className = 'rf-chat-mentions-menu rf-hidden';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', 'Chat mention suggestions');
    menu.addEventListener('mousedown', (ev) => {
      // Keep input focus while clicking a suggestion.
      ev.preventDefault();
    });
    if (form) form.appendChild(menu);
    else input.parentElement?.appendChild(menu);
    mentionMenuEl = menu;
    return menu;
  }

  function hideMentionMenu(): void {
    if (!mentionMenuEl) return;
    mentionMenuEl.classList.add('rf-hidden');
    mentionMenuEl.innerHTML = '';
    mentionVisibleOptions = [];
    mentionActiveIndex = 0;
  }

  function getActiveMentionQuery(input: HTMLTextAreaElement): { start: number; end: number; query: string } | null {
    const caret = typeof input.selectionStart === 'number' ? input.selectionStart : input.value.length;
    const left = input.value.slice(0, caret);
    const atIdx = left.lastIndexOf('@');
    if (atIdx < 0) return null;
    if (atIdx > 0) {
      const prev = left.charAt(atIdx - 1);
      if (!/\s|[([{"'`]/.test(prev)) return null;
    }
    const afterAt = left.slice(atIdx + 1);
    if (afterAt.indexOf('\n') >= 0 || /\s/.test(afterAt)) return null;
    if (!/^[a-z0-9-]*$/i.test(afterAt)) return null;
    return { start: atIdx, end: caret, query: afterAt.toLowerCase() };
  }

  function applyMentionSelection(input: HTMLTextAreaElement, option: (typeof CHAT_MENTION_OPTIONS)[number]): void {
    const q = getActiveMentionQuery(input);
    if (!q) return;
    const before = input.value.slice(0, q.start);
    const after = input.value.slice(q.end);
    const insert = option.token + ' ';
    input.value = before + insert + after;
    const pos = before.length + insert.length;
    input.focus();
    input.setSelectionRange(pos, pos);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    hideMentionMenu();
  }

  function renderMentionMenu(input: HTMLTextAreaElement): void {
    const q = getActiveMentionQuery(input);
    if (!q) {
      hideMentionMenu();
      return;
    }
    const menu = ensureMentionMenu(input);
    const nextOptions = CHAT_MENTION_OPTIONS.filter((opt) => {
      if (!q.query) return true;
      const key = opt.token.slice(1).toLowerCase();
      return key.startsWith(q.query);
    });
    if (nextOptions.length === 0) {
      hideMentionMenu();
      return;
    }
    mentionVisibleOptions = nextOptions;
    mentionActiveIndex = Math.max(0, Math.min(mentionActiveIndex, nextOptions.length - 1));
    menu.innerHTML = nextOptions
      .map((opt, idx) => {
        const active = idx === mentionActiveIndex ? ' rf-chat-mention-item-active' : '';
        return (
          '<button type="button" class="rf-chat-mention-item' + active + '" data-idx="' + idx + '" role="option" aria-selected="' + (idx === mentionActiveIndex ? 'true' : 'false') + '">' +
          '<span class="rf-chat-mention-token">' + escapeHtml(opt.token) + '</span>' +
          '<span class="rf-chat-mention-subtitle">' + escapeHtml(opt.subtitle) + '</span>' +
          '</button>'
        );
      })
      .join('');
    menu.querySelectorAll('.rf-chat-mention-item').forEach((node) => {
      node.addEventListener('click', () => {
        const idx = Number((node as HTMLElement).getAttribute('data-idx') || 0);
        const selected = mentionVisibleOptions[idx];
        if (!selected) return;
        applyMentionSelection(input, selected);
      });
    });
    menu.classList.remove('rf-hidden');
  }

  function deleteMessage(msgId: string): void {
    if (!msgId) return;
    const presId = adapter.getPresId();
    const slideIndex = adapter.getSlideIndex();
    if (typeof storage.deleteMessage !== 'function') return;
    Promise.resolve(storage.deleteMessage(presId, slideIndex, msgId))
      .then(() => renderMessages())
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        showFormError(msg || 'Could not delete message');
      });
  }

  function showDeleteConfirm(msgId: string): void {
    const existing = document.querySelector('.rf-chat-delete-confirm');
    if (existing && existing.parentElement) existing.parentElement.removeChild(existing);

    const overlay = document.createElement('div');
    overlay.className = 'rf-chat-delete-confirm';
    overlay.innerHTML =
      '<div class="rf-chat-delete-dialog">' +
      '<p class="rf-chat-delete-text">Delete this message?</p>' +
      '<div class="rf-chat-delete-actions">' +
      '<button type="button" class="rf-chat-delete-cancel">Cancel</button>' +
      '<button type="button" class="rf-chat-delete-ok">Delete</button>' +
      '</div>' +
      '</div>';

    const panel = drawerView.getChatPanel?.() || null;
    if (panel) panel.appendChild(overlay);
    else document.body.appendChild(overlay);

    const close = (): void => {
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    };

    const cancelBtn = overlay.querySelector('.rf-chat-delete-cancel') as HTMLButtonElement | null;
    const okBtn = overlay.querySelector('.rf-chat-delete-ok') as HTMLButtonElement | null;
    if (cancelBtn) cancelBtn.addEventListener('click', close);
    if (okBtn) {
      okBtn.addEventListener('click', () => {
        close();
        deleteMessage(msgId);
      });
    }
    overlay.addEventListener('click', (ev) => {
      if (ev.target === overlay) close();
    });
  }

  function renderMessages(): void {
    const presId = adapter.getPresId();
    const slideIndex = adapter.getSlideIndex();
    const messages = storage.getMessages(presId, slideIndex);
    const k = keyFor(presId, slideIndex);
    drawerOpen = drawerView.getDrawerElement().classList.contains('rf-open');
    const currentUid = adapter.getCurrentUser()?.uid || '';

    const maxCreatedAtForToast = messages.reduce((max, m) => {
      const ts = Number(m.createdAt ?? 0);
      return ts > max ? ts : max;
    }, 0);
    const hasToastBaseline = lastProcessedChatToastMaxCreatedAtByKey.has(k);
    const prevToastMax = hasToastBaseline ? lastProcessedChatToastMaxCreatedAtByKey.get(k)! : null;
    const el = getChatMessagesEl();

    chatToastVerbose('renderMessages', {
      slideKey: k,
      messageCount: messages.length,
      maxCreatedAtForToast,
      prevToastMax: prevToastMax === null ? '(no baseline)' : prevToastMax,
      hasChatListEl: !!el,
      drawerOpen
    });

    /**
     * Any message newer than the last processed high-water mark: show floating 💬 for the newest one in that batch
     * (your messages too). Haptic only for incoming (someone else) so sending from this device does not buzz.
     */
    if (!hasToastBaseline) {
      // Defer until we see a non-empty list so an initial [] snapshot does not pin baseline at 0
      // (which would fire a 💬 toast for the whole thread on the next render after refresh).
      if (messages.length > 0) {
        lastProcessedChatToastMaxCreatedAtByKey.set(k, maxCreatedAtForToast);
        chatToastVerbose('toast baseline init', { maxCreatedAtForToast });
      } else {
        chatToastVerbose('toast baseline deferred (empty snapshot)', { slideKey: k });
      }
    } else if (maxCreatedAtForToast > prevToastMax!) {
      const newcomers = messages.filter((m) => (Number(m.createdAt) || 0) > prevToastMax!);
      lastProcessedChatToastMaxCreatedAtByKey.set(k, maxCreatedAtForToast);
      if (newcomers.length > 0) {
        const toastFrom = newcomers[newcomers.length - 1];
        const ownerId = String(toastFrom.userId || toastFrom.uid || '');
        const fromSelf = !!currentUid && ownerId === currentUid;
        const authorHint = fromSelf
          ? 'You'
          : toDisplayName(toastFrom.displayName, ownerId).slice(0, 48) || ownerId || 'peer';
        chatToastVerbose('showRemoteEmoteToast', { emoji: CHAT_MESSAGE_TOAST_EMOJI, ownerId, fromSelf });
        if (typeof console !== 'undefined' && console.info) {
          console.info('[lectura][chat] Floating 💬 toast —', fromSelf ? 'your message' : `from ${authorHint}`);
        }
        if (!fromSelf) {
          lecturaHapticChatIncoming();
        }
        showRemoteEmoteToast(CHAT_MESSAGE_TOAST_EMOJI, {
          label: `Chat · ${authorHint}`,
          durationMs: 3800,
          scale: 1.05
        });
      }
    } else {
      chatToastVerbose('no toast (max createdAt not advanced)', { maxCreatedAtForToast, prevToastMax });
    }

    if (!el) {
      chatToastVerbose('skip DOM render (no chat messages element yet)');
      return;
    }

    bindChatScrollEvents();

    const maxCreatedAt = messages.reduce((max, m) => {
      const ts = Number(m.createdAt ?? 0);
      return ts > max ? ts : max;
    }, 0);

    if (!chatFirstSnapshotHadMessagesByKey.has(k)) {
      chatFirstSnapshotHadMessagesByKey.set(k, messages.length > 0);
    }
    const firstSnapshotHadMessages = chatFirstSnapshotHadMessagesByKey.get(k) === true;

    const readBootstrapKey = userReadKey(presId, slideIndex);
    let readWatermark = getReadWatermark(presId, slideIndex);
    if (drawerOpen) {
      readWatermark = maxCreatedAt;
      setReadWatermark(presId, slideIndex, readWatermark);
      bootstrappedByKey.add(readBootstrapKey);
    } else if (!bootstrappedByKey.has(readBootstrapKey) && readWatermark <= 0) {
      // First load with no saved read cursor: treat existing backlog as already seen, but only when
      // the first snapshot already contained messages. If we saw [] first (hydration), wait — otherwise
      // we persist watermark 0, mark bootstrapped, and later every message is "unread" after refresh.
      if (firstSnapshotHadMessages && messages.length > 0) {
        readWatermark = maxCreatedAt;
        setReadWatermark(presId, slideIndex, readWatermark);
        bootstrappedByKey.add(readBootstrapKey);
      }
    }

    const wasNearBottom = isChatNearBottom(el);
    if (messages.length === 0) {
      const suggestions = getStableEmptySuggestions(presId, slideIndex);
      el.innerHTML =
        '<div class="rf-chat-empty-state">' +
        '<p class="rf-chat-empty-title">No messages yet.</p>' +
        '<p class="rf-chat-empty-subtitle">Be the first to kick off this slide conversation.</p>' +
        '<div class="rf-chat-empty-actions">' +
        '<button type="button" class="rf-chat-empty-randomize-btn" title="Shuffle suggestions" aria-label="Shuffle suggestions">' +
        '<span class="rf-chat-empty-randomize-emoji" aria-hidden="true">🎲</span>' +
        '</button>' +
        '</div>' +
        '<div class="rf-chat-empty-suggestions">' +
        renderSuggestionButtonsHtml(suggestions) +
        '</div>' +
        '</div>';
    } else {
      el.innerHTML = messages
        .map((m) => {
          const id = escapeHtml((m.id ?? '').toString());
          const ownerId = m.userId || m.uid;
          const author = escapeHtml(toDisplayName(m.displayName, ownerId));
          const role = roleService?.getAuthorRole?.(m) || (m.authorRole === 'admin' || m.authorRole === 'presenter' ? m.authorRole : null);
          const roleBadge =
            role === 'admin'
              ? '<span class="rf-chat-msg-author-role rf-chat-msg-author-role-admin" title="Lectura Support" aria-label="Lectura Support"><img class="rf-chat-msg-author-role-icon rf-chat-msg-author-role-icon-logo" src="' + escapeHtml(getLecturaFaviconHref()) + '" alt="" /></span>'
              : role === 'presenter'
                ? '<span class="rf-chat-msg-author-role rf-chat-msg-author-role-presenter" title="Presenter" aria-label="Presenter">🎤</span>'
                : '';
          const authorColor = String(m.authorColor || '').trim();
          const safeAuthorColor = /^[0-9]{1,3}\s*,\s*[0-9]{1,3}\s*,\s*[0-9]{1,3}$/.test(authorColor) ? authorColor : '';
          const authorStyle = safeAuthorColor ? ' style="color: rgb(' + escapeHtml(safeAuthorColor) + ')"' : '';
          const text = (m.text || '').toString();
          const { label: timeLabel, full: fullTimeLabel } = formatMessageTime(m.createdAt);
          const canDelete = !!m.id && (roleService ? roleService.canDeleteMessage(m) : !!currentUid && !!ownerId && ownerId === currentUid);
          const replyQuote =
            m.replyToAuthor || m.replyToText
              ? '<div class="rf-chat-msg-reply-quote">Replying to ' +
                escapeHtml((m.replyToAuthor || 'Unknown').toString()) +
                ': ' +
                escapeHtml((m.replyToText || '').trim().slice(0, 60) + ((m.replyToText || '').trim().length > 60 ? '…' : '')) +
                '</div>'
              : '';
          const isAiMsg = m.userId === 'lectura-ai';
          const looksLikeMarkdown =
            isAiMsg ||
            /```[\s\S]*?```|`[^`\n]+`|\*\*[\s\S]*?\*\*/m.test(text);

          let bodyHtml: string;
          if (isAiMsg) {
            const marker = '\nSources\n';
            const markerIdx = text.indexOf(marker);
            if (markerIdx === -1) {
              bodyHtml = '<div class="rf-chat-msg-md">' + formatChatMarkdown(text) + '</div>';
            } else {
              const main = text.slice(0, markerIdx).trimEnd();
              const sources = text.slice(markerIdx + marker.length).trim();
              const mainHtml = '<div class="rf-chat-msg-md">' + formatChatMarkdown(main) + '</div>';
              const sourcesHtml = sources
                ? '<button type="button" class="rf-chat-msg-sources-toggle" aria-expanded="false">Sources</button>' +
                  '<pre class="rf-chat-msg-sources rf-hidden">' +
                  escapeHtml(sources) +
                  '</pre>'
                : '';
              bodyHtml = mainHtml + sourcesHtml;
            }
          } else if (looksLikeMarkdown) {
            bodyHtml = '<div class="rf-chat-msg-md">' + formatChatMarkdown(text) + '</div>';
          } else {
            bodyHtml = '<span class="rf-chat-msg-text">' + escapeHtml(text) + '</span>';
          }

          return (
            '<div class="rf-chat-msg" data-id="' +
            id +
            '" data-author="' +
            escapeHtml(author) +
            '" data-text="' +
            escapeHtml(text) +
            '">' +
            '<div class="rf-chat-msg-meta"><div class="rf-chat-msg-meta-main"><span class="rf-chat-msg-author"' + authorStyle + '>' +
            author +
            '</span>' + roleBadge + '</div><div class="rf-chat-msg-meta-right">' +
            (timeLabel ? '<span class="rf-chat-msg-time" title="' + escapeHtml(fullTimeLabel) + '">' + escapeHtml(timeLabel) + '</span>' : '') +
            '<button type="button" class="rf-chat-msg-reply-btn" title="Reply" aria-label="Reply" data-msg-id="' +
            id +
            '" data-msg-author="' +
            escapeHtml(author) +
            '" data-msg-text="' +
            escapeHtml(text) +
            '" data-msg-from-ai="' +
            (isAiMsg ? '1' : '0') +
            '">↩</button>' +
            (canDelete ? '<button type="button" class="rf-chat-msg-delete-btn" data-msg-id="' + id + '">&times;</button>' : '') +
            '</div></div>' +
            replyQuote +
            bodyHtml +
            '</div>'
          );
        })
        .join('');
    }

    el.querySelectorAll('.rf-chat-msg-reply-btn').forEach((node) => {
      node.addEventListener('click', () => {
        const btn = node as HTMLElement;
        replyingTo = {
          id: btn.getAttribute('data-msg-id') || '',
          author: btn.getAttribute('data-msg-author') || 'Unknown',
          text: btn.getAttribute('data-msg-text') || '',
          fromAi: btn.getAttribute('data-msg-from-ai') === '1'
        };
        updateReplyPreview();
        focusChatInputLikeUserClick();
      });
    });
    el.querySelectorAll('.rf-chat-msg-delete-btn').forEach((node) => {
      node.addEventListener('click', () => {
        const btn = node as HTMLElement;
        const msgId = btn.getAttribute('data-msg-id') || '';
        if (!msgId) return;
        showDeleteConfirm(msgId);
          });
        });

    el.querySelectorAll('.rf-chat-msg-sources-toggle').forEach((node) => {
      node.addEventListener('click', () => {
        const btn = node as HTMLButtonElement;
        const msgEl = btn.closest('.rf-chat-msg') as HTMLElement | null;
        const panel = msgEl?.querySelector('.rf-chat-msg-sources') as HTMLElement | null;
        if (!panel) return;
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.classList.toggle('rf-hidden', expanded);
      });
    });

    el.querySelectorAll('.rf-chat-md-copy').forEach((node) => {
      node.addEventListener('click', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const btn = node as HTMLButtonElement;
        const b64 = btn.getAttribute('data-code-b64') || '';
        let text = '';
        try {
          text = decodeURIComponent(escape(atob(b64)));
        } catch {
          return;
        }
        if (!text) return;
        const copy = async (): Promise<void> => {
          try {
            await navigator.clipboard.writeText(text);
            btn.classList.add('rf-chat-md-copied');
            btn.setAttribute('aria-label', 'Copied');
            window.setTimeout(() => {
              btn.classList.remove('rf-chat-md-copied');
              btn.setAttribute('aria-label', 'Copy code');
            }, 2000);
          } catch {
            try {
              const ta = document.createElement('textarea');
              ta.value = text;
              ta.style.position = 'fixed';
              ta.style.left = '-9999px';
              document.body.appendChild(ta);
              ta.select();
              document.execCommand('copy');
              document.body.removeChild(ta);
              btn.classList.add('rf-chat-md-copied');
              window.setTimeout(() => btn.classList.remove('rf-chat-md-copied'), 2000);
            } catch {
              /* ignore */
            }
          }
        };
        void copy();
      });
    });
    el.querySelectorAll('.rf-chat-empty-suggestion').forEach((node) => {
      node.addEventListener('click', () => {
        const btn = node as HTMLElement;
        const suggestion = btn.getAttribute('data-suggestion') || '';
        if (!suggestion) return;
        const input = drawerView.getChatInput?.() as HTMLTextAreaElement | null | undefined;
        if (!input) return;
        input.value = suggestion;
        input.focus();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
    const randomizeBtn = el.querySelector('.rf-chat-empty-randomize-btn') as HTMLButtonElement | null;
    const suggestionsWrap = el.querySelector('.rf-chat-empty-suggestions') as HTMLElement | null;
    if (messages.length === 0 && suggestionsWrap && animateEmptySuggestionsOnNextRender) {
      animateEmptySuggestionsOnNextRender = false;
      suggestionsWrap.classList.remove('rf-chat-empty-suggestions-randomizing');
      void suggestionsWrap.offsetWidth;
      suggestionsWrap.classList.add('rf-chat-empty-suggestions-randomizing');
      window.setTimeout(() => {
        suggestionsWrap.classList.remove('rf-chat-empty-suggestions-randomizing');
      }, 380);
    }
    if (randomizeBtn && suggestionsWrap) {
      randomizeBtn.addEventListener('click', () => {
        if (randomizeBtn.disabled) return;
        randomizeBtn.disabled = true;
        randomizeBtn.classList.add('rf-randomizing');

        suggestionsWrap.classList.remove('rf-chat-empty-suggestions-randomizing');
        void suggestionsWrap.offsetWidth;
        suggestionsWrap.classList.add('rf-chat-empty-suggestions-randomizing');

        window.setTimeout(() => {
          suggestionsWrap.innerHTML = renderSuggestionButtonsHtml(reshuffleEmptySuggestions(presId, slideIndex));
          suggestionsWrap.querySelectorAll('.rf-chat-empty-suggestion').forEach((node) => {
            node.addEventListener('click', () => {
              const btn = node as HTMLElement;
              const suggestion = btn.getAttribute('data-suggestion') || '';
              if (!suggestion) return;
              const input = drawerView.getChatInput?.() as HTMLTextAreaElement | null | undefined;
              if (!input) return;
              input.value = suggestion;
              input.focus();
              input.dispatchEvent(new Event('input', { bubbles: true }));
            });
          });
        }, 140);

        window.setTimeout(() => {
          randomizeBtn.disabled = false;
          randomizeBtn.classList.remove('rf-randomizing');
          suggestionsWrap.classList.remove('rf-chat-empty-suggestions-randomizing');
        }, 380);
      });
    }

    if (drawerOpen || wasNearBottom) {
      pinChatToBottomNow();
    }
    updateScrollBottomButtonVisibility();

    const unread = messages.reduce((acc, m) => {
      const ts = Number(m.createdAt ?? 0);
      return ts > readWatermark ? acc + 1 : acc;
    }, 0);
    totalCountByKey.set(k, messages.length);
    unreadCountByKey.set(k, unread);
    updateBadges();
  }

  function startSlideSubscription(): void {
    if (unsubscribeMessages) {
      unsubscribeMessages();
      unsubscribeMessages = null;
    }
    if (!storage.subscribeMessages) return;
    const presId = adapter.getPresId();
    const slideIndex = adapter.getSlideIndex();
    unsubscribeMessages = storage.subscribeMessages(
      presId,
      slideIndex,
      () => {
        renderMessages();
      },
      (err) => {
        showFormError(err.message || 'Failed to load messages');
      }
    );
  }

  function showFormError(message: string): void {
    const el = drawerView.getChatFormError?.();
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('rf-hidden', !message);
  }

  function autosizeChatInput(input: HTMLTextAreaElement | null | undefined): void {
    if (!input) return;
    const computed = window.getComputedStyle(input);
    const minHeight = Number.parseFloat(computed.minHeight || '') || 36;
    const maxHeight = Number.parseFloat(computed.maxHeight || '') || 100;

    input.style.height = 'auto';
    const nextHeight = Math.min(Math.max(input.scrollHeight, minHeight), maxHeight);
    input.style.height = String(nextHeight) + 'px';
    input.style.overflowY = input.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  function focusChatInputLikeUserClick(): void {
    const input = drawerView.getChatInput?.() as HTMLTextAreaElement | null | undefined;
    if (!input) return;
    try {
      input.scrollIntoView({ block: 'nearest' });
    } catch {}
    const mouseInit: MouseEventInit = { bubbles: true, cancelable: true, view: window };
    input.dispatchEvent(new MouseEvent('mousedown', mouseInit));
    input.dispatchEvent(new MouseEvent('mouseup', mouseInit));
    input.dispatchEvent(new MouseEvent('click', mouseInit));
    input.focus();
    try {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    } catch {}
    window.setTimeout(() => {
      input.focus();
      try {
        const end = input.value.length;
        input.setSelectionRange(end, end);
      } catch {}
    }, 0);
  }

  function sendMessage(): void {
    const user = adapter.getCurrentUser();
    const textEl = drawerView.getChatInput?.() as HTMLTextAreaElement | null | undefined;
    const text = (textEl?.value ?? '').trim();
    if (!text) {
      showFormError('Enter a message');
      return;
    }
    showFormError('');
    const presId = adapter.getPresId();
    const slideIndex = adapter.getSlideIndex();
    const trimmed = text.trim();
    /** Email: @lectura first, else @presenter. Never AI when either appears anywhere. */
    const mentionPriority: 'lectura' | 'presenter' | null = hasMentionToken(trimmed, '@lectura')
      ? 'lectura'
      : hasMentionToken(trimmed, '@presenter')
        ? 'presenter'
        : null;
    const questionAfterAtAi = extractAiQuestionAfterAtAi(trimmed);
    const replyThreadToLecturaAi = isReplyingToLecturaAi(replyingTo);
    let isAi = false;
    let aiQuestion = '';
    if (!mentionPriority) {
      if (questionAfterAtAi !== null) {
        isAi = true;
        aiQuestion = questionAfterAtAi;
      } else if (replyThreadToLecturaAi && trimmed) {
        isAi = true;
        aiQuestion = trimmed;
      }
    }
    const sendBtn = drawerView.getChatSendBtn?.() as HTMLButtonElement | null | undefined;
    const originalSendDisabled = !!sendBtn?.disabled;
    if (isAi && !aiQuestion) {
      showFormError(
        replyThreadToLecturaAi ? 'Enter a follow-up for Lectura AI.' : 'Add text after @ai (e.g. @ai your question).'
      );
      return;
    }

    let latestUserMessageId = '';
    Promise.resolve(roleService?.refresh?.())
      .catch(() => undefined)
      .then(() => {
        const payload: { userId: string; displayName: string; authorEmail?: string; authorRole?: 'presenter' | 'admin'; text: string; authorColor?: string; replyToId?: string; replyToAuthor?: string; replyToText?: string } = {
          userId: user?.uid || 'anon',
          displayName: user?.displayName || user?.email || user?.uid || 'Anonymous',
          text
        };
        if (user?.email) payload.authorEmail = user.email;
        const senderRole = roleService?.getRole?.();
        if (senderRole === 'admin' || senderRole === 'presenter') payload.authorRole = senderRole;
        const myColor = String(adapter.getHighlightRgb?.() || '').trim();
        if (myColor) payload.authorColor = myColor;
        if (replyingTo?.id) {
          payload.replyToId = replyingTo.id;
          payload.replyToAuthor = replyingTo.author;
          payload.replyToText = replyingTo.text.slice(0, 120);
        }
        return storage.addMessage(presId, slideIndex, payload);
      })
      .then((userMessageId) => {
        latestUserMessageId = String(userMessageId || '');
        // Clear input right after the user message is stored (Enter vs click both feel instant).
        replyingTo = null;
        updateReplyPreview();
        if (textEl) {
          textEl.value = '';
          autosizeChatInput(textEl);
          hideMentionMenu();
        }
        renderMessages();
        return userMessageId;
      })
      .then(async (userMessageId) => {
        const mentionKind = !isAi ? mentionPriority : null;
        if (mentionKind && chatMentionNotify) {
          const supportReplyAuthor = toDisplayName(
            user?.displayName || user?.email || '',
            user?.uid || 'you'
          );
          const supportReplySnippet =
            text.trim().slice(0, 120) + (text.trim().length > 120 ? '…' : '');
          const postSupportLine = (body: string): Promise<void> =>
            Promise.resolve(
              storage.addMessage(presId, slideIndex, {
                userId: 'lectura-support',
                displayName: 'Lectura support',
                authorRole: 'admin',
                text: body.slice(0, 2000),
                ...(userMessageId
                  ? {
                      replyToId: userMessageId,
                      replyToAuthor: supportReplyAuthor,
                      replyToText: supportReplySnippet
                    }
                  : {})
              })
            ).then(
              () => {
                window.setTimeout(() => renderMessages(), 0);
              },
              () => undefined
            );
          try {
            const slideUrl = typeof window !== 'undefined' ? window.location.href : '';
            const presTitle =
              typeof document !== 'undefined'
                ? String(document.title || '')
                    .replace(/\s*\|\s*Lectura.*$/i, '')
                    .trim()
                    .slice(0, 300)
                : '';
            const { sent } = await chatMentionNotify.notify({
              kind: mentionKind,
              presentationId: presId,
              slideIndex,
              slideUrl,
              messageText: text,
              presentationTitle: presTitle || undefined,
              submitterDisplayName: user?.displayName || (user?.email ? user.email.split('@')[0] : '') || undefined,
              submitterEmail: user?.email || undefined
            });
            if (sent) {
              const confirmText =
                mentionKind === 'lectura' ? "We've notified the support team." : "We've notified the presenters.";
              await postSupportLine(confirmText);
            } else {
              await postSupportLine(
                "Couldn't send email from this environment. Use the live deck while signed in (not guest) to reach presenters or support."
              );
            }
          } catch (notifyErr) {
            const nmsg = notifyErr instanceof Error ? notifyErr.message : String(notifyErr || '');
            showFormError('');
            await postSupportLine(
              "Couldn't deliver that notification: " + (nmsg.trim() || 'Something went wrong.')
            );
          }
        }
        if (!isAi) return;
        if (!ai?.queryPresentation) {
          showFormError('AI is not available in this environment.');
          return;
        }
        if (sendBtn) sendBtn.disabled = true;
        showFormError('');

        const messages = storage.getMessages(presId, slideIndex) || [];
        const recent = messages.slice(-10);
        const chatContextParts: string[] = [];
        recent.forEach((m) => {
          const role = m.userId === 'lectura-ai' ? 'AI' : 'User';
          const name = m.displayName || (m.authorEmail || '').split('@')[0] || role;
          const body = String(m.text || '').trim().slice(0, 400);
          if (!body) return;
          chatContextParts.push(`${role} (${name}): ${body}`);
        });
        const slideBlock = buildSlideContextForAi(adapter);
        const slideHeader = `Current slide: ${slideIndex + 1}${slideBlock ? `\n${slideBlock}` : ''}`;
        const chatContext = chatContextParts.length
          ? `${slideHeader}\nRecent messages:\n` + chatContextParts.join('\n')
          : slideHeader;

        const result = await ai.queryPresentation(presId, {
          question: aiQuestion,
          topK: 6,
          chatContext,
          slideIndex
        });
        const citations = Array.isArray(result.citations) ? result.citations : [];
        const formatted = formatAiAnswer(result.answer, citations);
        const replyAuthor = toDisplayName(
          user?.displayName || user?.email || '',
          user?.uid || 'you'
        );
        const replySnippet = text.trim().slice(0, 120) + (text.trim().length > 120 ? '…' : '');
        await storage.addMessage(presId, adapter.getSlideIndex(), {
          userId: 'lectura-ai',
          displayName: 'Lectura AI',
          authorRole: 'admin',
          text: formatted,
          ...(userMessageId
            ? {
                replyToId: userMessageId,
                replyToAuthor: replyAuthor,
                replyToText: replySnippet
              }
            : {})
        });
      })
      .then(() => {
        renderMessages();
      })
      .catch((err) => {
        const code =
          typeof err === 'object' && err !== null && 'code' in err
            ? String((err as { code?: string }).code || '')
            : '';
        const msg = err instanceof Error ? err.message : String(err);
        const raw = String(msg || '').trim();
        const cleaned = raw
          .replace(/^FirebaseError:\s*/i, '')
          .replace(/^Firebase:\s*[^:]*:\s*/i, '')
          .replace(/^Firebase:\s*/i, '')
          .trim();
        const isLimitError =
          isAi &&
          (code === 'functions/resource-exhausted' || /limit|resource-exhausted/i.test(raw));
        if (isLimitError) {
          const serverText = cleaned || raw;
          const useServer =
            code === 'functions/resource-exhausted' &&
            serverText.length > 0 &&
            serverText.length < 800 &&
            !/^INTERNAL$/i.test(serverText);
          const limitMessage = useServer
            ? serverText
            : /Daily AI /i.test(serverText) && serverText.length < 800
              ? serverText
              : "I’ve hit the AI limit for this deck today — I can’t answer more until tomorrow. If something’s urgent, try asking a presenter or come back after the daily reset (UTC).";
          const replyAuthor = toDisplayName(
            user?.displayName || user?.email || '',
            user?.uid || 'you'
          );
          const replySnippet = text.trim().slice(0, 120) + (text.trim().length > 120 ? '…' : '');
          Promise.resolve(
            storage.addMessage(presId, slideIndex, {
              userId: 'lectura-ai',
              displayName: 'Lectura AI',
              authorRole: 'admin',
              text: limitMessage,
              ...(latestUserMessageId
                ? {
                    replyToId: latestUserMessageId,
                    replyToAuthor: replyAuthor,
                    replyToText: replySnippet
                  }
                : {})
            })
          ).then(
            () => {
              showFormError('');
              renderMessages();
            },
            () => {
              showFormError(limitMessage);
            }
          );
          return;
        }
        showFormError(msg || 'Failed to send message');
      })
      .finally(() => {
        if (sendBtn) sendBtn.disabled = originalSendDisabled;
      });
  }

  function formatAiAnswer(answer: string, citations: AIResourceCitation[]): string {
    const safeAnswer = String(answer || '').trim() || "I don't have enough context from the attached resources to answer that yet.";
    if (!citations.length) return safeAnswer;

    // Deduplicate citations by resource (defensive, backend already tries to do this).
    const unique: AIResourceCitation[] = [];
    const seen = new Set<string>();
    citations.forEach((c) => {
      const key = `${c.resourceId}::${c.resourceType}::${c.title || ''}::${c.url || ''}::${c.fileName || ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(c);
    });
    if (!unique.length) return safeAnswer;

    const lines: string[] = [];
    lines.push(safeAnswer);
    lines.push('');
    lines.push('Sources');

    const maxInline = 3;
    unique.slice(0, maxInline).forEach((c, idx) => {
      const title = String(c.title || 'Resource').trim();
      const fileName = String(c.fileName || '').trim();
      const url = String(c.url || '').trim();
      const ref = c.resourceType === 'file' ? (fileName || title) : (url || title);
      const same =
        ref === title ||
        (c.resourceType === 'file' && fileName && title.toLowerCase() === fileName.toLowerCase()) ||
        (c.resourceType !== 'file' && url && title.toLowerCase() === url.toLowerCase());
      const line = same ? `${idx + 1}. ${title}` : `${idx + 1}. ${title} — ${ref}`;
      lines.push(line);
    });
    if (unique.length > maxInline) {
      const remaining = unique.length - maxInline;
      lines.push(`(+${remaining} more source${remaining === 1 ? '' : 's'} not shown)`);
    }
    return lines.join('\n');
  }

  on(EVENTS.SLIDE_CHANGED, () => {
    animateEmptySuggestionsOnNextRender = true;
    emptySuggestionsByKey.delete(keyFor(adapter.getPresId(), adapter.getSlideIndex()));
    drawerView.setSlideNumber(adapter.getSlideIndex());
    startSlideSubscription();
    roleService?.refresh().catch(() => {});
    renderMessages();
  });

  on(EVENTS.USER_CHANGED, () => {
    showFormError('');
    startSlideSubscription();
    roleService?.refresh().finally(() => {
      renderMessages();
    });
  });

  on(EVENTS.OPEN_CHAT_COMPOSER, (text: unknown) => {
    const t = String(text ?? '').trim();
    const input = drawerView.getChatInput?.() as HTMLTextAreaElement | null | undefined;
    if (!input) return;
    input.value = t;
    showFormError('');
    autosizeChatInput(input);
    renderMentionMenu(input);
    window.requestAnimationFrame(() => {
      input.focus();
      const len = input.value.length;
      try {
        input.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  });

  on(EVENTS.DRAWER_OPEN, (open: unknown) => {
    drawerOpen = !!open;
    if (drawerOpen) {
      const presId = adapter.getPresId();
      const slideIndex = adapter.getSlideIndex();
      const k = keyFor(presId, slideIndex);
      const messages = storage.getMessages(presId, slideIndex);
      const maxCreatedAt = messages.reduce((max, m) => {
        const ts = Number(m.createdAt ?? 0);
        return ts > max ? ts : max;
      }, 0);
      setReadWatermark(presId, slideIndex, maxCreatedAt);
      unreadCountByKey.set(k, 0);
      bootstrappedByKey.add(userReadKey(presId, slideIndex));
      updateBadges();
      const chatMessagesEl = getChatMessagesEl();
      if (chatMessagesEl) {
        pinChatToBottomNow();
      }
    }
    updateScrollBottomButtonVisibility();
  });

  const cancelReplyBtn = drawerView.getChatReplyPreviewCancel?.();
  if (cancelReplyBtn) {
    cancelReplyBtn.addEventListener('click', () => {
      replyingTo = null;
      updateReplyPreview();
    });
  }

  const sendBtn = drawerView.getChatSendBtn?.();
  if (sendBtn) sendBtn.addEventListener('click', sendMessage);
  const chatInput = drawerView.getChatInput?.();
  if (chatInput) {
    autosizeChatInput(chatInput as HTMLTextAreaElement);
    chatInput.addEventListener('input', () => {
      showFormError('');
      const input = chatInput as HTMLTextAreaElement;
      autosizeChatInput(input);
      renderMentionMenu(input);
    });
    chatInput.addEventListener('keydown', (e: Event) => {
      const ke = e as KeyboardEvent;
      const input = chatInput as HTMLTextAreaElement;
      if (mentionVisibleOptions.length > 0 && mentionMenuEl && !mentionMenuEl.classList.contains('rf-hidden')) {
        if (ke.key === 'ArrowDown' || ke.key === 'ArrowUp') {
          ke.preventDefault();
          if (ke.key === 'ArrowDown') mentionActiveIndex = (mentionActiveIndex + 1) % mentionVisibleOptions.length;
          else mentionActiveIndex = (mentionActiveIndex - 1 + mentionVisibleOptions.length) % mentionVisibleOptions.length;
          renderMentionMenu(input);
          return;
        }
        if (ke.key === 'Enter' || ke.key === 'Tab') {
          ke.preventDefault();
          const selected = mentionVisibleOptions[mentionActiveIndex] || mentionVisibleOptions[0];
          if (selected) applyMentionSelection(input, selected);
          return;
        }
        if (ke.key === 'Escape') {
          ke.preventDefault();
          hideMentionMenu();
          return;
        }
      }
      if (ke.key === 'Enter' && !ke.shiftKey) {
        ke.preventDefault();
        sendMessage();
      }
    });
    chatInput.addEventListener('blur', () => {
      // Delay hide to allow option click handlers to run.
      window.setTimeout(() => hideMentionMenu(), 120);
    });
  }

  loadReadWatermarks();
  roleService?.refresh().catch(() => {});
  const unsubRoles = roleService?.subscribe?.(() => renderMessages());
  drawerView.setSlideNumber(adapter.getSlideIndex());
  startSlideSubscription();
  renderMessages();
  updateScrollBottomButtonVisibility();
  void unsubRoles;
}
